import React from 'react';
import ReactDOM from 'react-dom';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

if (process.env.NODE_ENV === 'development') {
  const shouldIgnoreResizeObserverError = (message: unknown) =>
    typeof message === 'string' && message.includes('ResizeObserver loop');

  // Completely disable the CRA error overlay in development.
  const overlayHook = (window as typeof window & {
    __REACT_ERROR_OVERLAY_GLOBAL_HOOK__?: {
      onError?: (err: unknown) => void;
      onUnhandledRejection?: (err: unknown) => void;
      iframeReady?: () => void;
    };
  }).__REACT_ERROR_OVERLAY_GLOBAL_HOOK__;
  if (overlayHook) {
    overlayHook.onError = () => {};
    overlayHook.onUnhandledRejection = () => {};
    overlayHook.iframeReady = () => {};
  }

  const removeOverlayIframes = () => {
    document.querySelectorAll('iframe').forEach((node) => {
      const iframe = node as HTMLIFrameElement;
      const srcdoc = iframe.getAttribute('srcdoc') || '';
      if (srcdoc.includes('react-error-overlay')) {
        iframe.remove();
      }
    });
  };

  const overlayObserver = new MutationObserver(() => {
    removeOverlayIframes();
  });
  overlayObserver.observe(document.body, { childList: true });
  removeOverlayIframes();

  if ('ResizeObserver' in window) {
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback: ResizeObserverCallback) {
        super((entries, observer) => {
          try {
            callback(entries, observer);
          } catch (err) {
            const message = err instanceof Error ? err.message : '';
            if (shouldIgnoreResizeObserverError(message)) return;
            throw err;
          }
        });
      }
    };
  }

  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    const [first] = args;
    if (shouldIgnoreResizeObserverError(first)) {
      return;
    }
    originalConsoleError(...args);
  };

  const originalOnError = window.onerror;
  window.onerror = (...args) => {
    const [message] = args;
    if (shouldIgnoreResizeObserverError(message)) return true;
    if (originalOnError) return originalOnError.apply(window, args);
    return false;
  };

  window.addEventListener(
    'error',
    (event) => {
      if (shouldIgnoreResizeObserverError(event.message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason ?? '');
    if (shouldIgnoreResizeObserverError(message)) {
      event.preventDefault();
    }
  });
}

ReactDOM.render(
  <React.StrictMode>
    <ThemeProvider
      theme={createTheme({
        palette: {
          background: {
            default: '#f1f3f5',
            paper: '#ffffff',
          },
        },
      })}
    >
      <CssBaseline />
      <App />
    </ThemeProvider>
  </React.StrictMode>,
  document.getElementById('root')
);

reportWebVitals();
